import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useHistory } from 'react-router';
import { HealtheraButton } from '../../../../healtheraComponents';
import { StyledPaperContainer } from '../../../PlusWebsiteEditor/PlusWebsiteEditor.styles';
import { StyledButtonsContainer } from '../../../Questionnaires/QuestionnairePage/components/QuestionViewerEditor/QuestionEditor/styles';
import { ClinicFormProps } from '../types';
import { postClinic, cloneClinic, CloneClinicRequest } from '../../../../apis/clinics/requests';
import { ClinicWithConditionsAndPromo } from '../../types';
import { SubmitErrorHandler } from 'react-hook-form';

const CreateClinicFooter = ({ form, selectedClinicToClone }: ClinicFormProps) => {
  return (
    <StyledPaperContainer>
      <StyledButtonsContainer>
        <CreateClinicButton form={form} selectedClinicToClone={selectedClinicToClone} />
      </StyledButtonsContainer>
    </StyledPaperContainer>
  );
};

const CreateClinicButton = ({ form, selectedClinicToClone }: ClinicFormProps) => {
  const { createClinic, isLoading: isCreateLoading } = useCreateClinic();
  const { cloneExistingClinic, isLoading: isCloneLoading } = useCloneClinic();
  const history = useHistory();

  const isLoading = isCreateLoading || isCloneLoading;

  const handleSave = async (clinic: ClinicWithConditionsAndPromo) => {
    if (selectedClinicToClone) {
      const cloneData: CloneClinicRequest = {
        sourceClinicId: selectedClinicToClone,
        clinic: {
          name: clinic.name,
          internal_prescribing: clinic.internal_prescribing,
          internal_product_charge: clinic.internal_product_charge,
          ...(clinic.fulfilment_type ? { fulfilment_type: clinic.fulfilment_type } : {})
        }
      };

      await cloneExistingClinic(cloneData);
      enqueueSnackbar('Clinic cloned successfully', { variant: 'success' });
    } else {
      await createClinic(clinic);
      enqueueSnackbar('Clinic created', { variant: 'success' });
    }

    history.goBack();
  };

  const handleInvalidForm: SubmitErrorHandler<ClinicWithConditionsAndPromo> = () => {
    enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
  };

  return (
    <HealtheraButton onClick={form.handleSubmit(handleSave, handleInvalidForm)} disabled={isLoading}>
      {selectedClinicToClone ? 'Clone Clinic' : 'Create Clinic'}
    </HealtheraButton>
  );
};

const useCreateClinic = () => {
  const mutation = useMutation({
    mutationFn: postClinic
  });

  const { mutateAsync: createClinic, isLoading } = mutation;

  return { createClinic, isLoading };
};

const useCloneClinic = () => {
  const mutation = useMutation({
    mutationFn: cloneClinic
  });

  const { mutateAsync: cloneExistingClinic, isLoading } = mutation;

  return { cloneExistingClinic, isLoading };
};

export default CreateClinicFooter;
